import React from "react";
import { Box } from "@mui/material";

export const LANDING_CONTAINER_SX = {
  maxWidth: "xl",
  width: "100%",
  mx: "auto",
  boxSizing: "border-box",
  px: { xs: 2, sm: 3, md: 8, lg: 14, xl: 18 },
};

/** Full-width section shell — use on landing page sections */
export const LANDING_SECTION_SX = {
  width: "100%",
  maxWidth: "100%",
  mx: "auto",
  overflow: "hidden",
};

/** xl container tuned for the sticky header (Figma ~54px side inset at 1440) */
export const HEADER_CONTAINER_SX = {
  maxWidth: "xl",
  mx: "auto",
  width: "100%",
  px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 7 },
};

export const SECTION_PADDING = {
  default: { py: { xs: 5, md: 8 } },
  compact: { py: { xs: 4, md: 6 } },
};

export const LANDING_FONT_FAMILY = "'Plus Jakarta Sans', sans-serif";

/** Apply on the landing page root so MUI components inherit Plus Jakarta Sans. */
export const LANDING_FONT_SX = {
  fontFamily: LANDING_FONT_FAMILY,
  "& .MuiTypography-root": { fontFamily: "inherit" },
  "& .MuiButton-root": { fontFamily: "inherit" },
  "& .MuiLink-root": { fontFamily: "inherit" },
};

export const sectionHeadingSx = {
  fontWeight: 700,
  color: "primary.darker",
  fontSize: { xs: "1.75rem", md: "2.25rem" },
  lineHeight: 1.15,
  letterSpacing: "-0.02em",
};

export const sectionSubheadingSx = {
  color: "text.secondary",
  mt: 1.25,
  fontSize: { xs: "0.95rem", md: "1rem" },
  lineHeight: 1.55,
};

export const primaryCtaSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  bgcolor: "secondary.main",
  color: "common.white",
  px: "24px",
  height: 48,
  borderRadius: "12px",
  fontSize: "15px",
  fontWeight: 600,
  textDecoration: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  lineHeight: 1,
  whiteSpace: "nowrap",
  transition: "background-color 0.2s ease",
  width: { xs: "100%", sm: "auto" },
  "&:hover": {
    bgcolor: "secondary.dark",
    color: "common.white",
  },
};

export const secondaryCtaSx = {
  ...primaryCtaSx,
  bgcolor: "common.white",
  color: "primary.darker",
  border: "1px solid",
  borderColor: "grey.300",
  "&:hover": {
    bgcolor: "grey.50",
    borderColor: "grey.400",
    color: "primary.darker",
  },
};

export const outlineLightCtaSx = {
  ...primaryCtaSx,
  bgcolor: "transparent",
  color: "common.white",
  border: "2px solid rgba(255,255,255,0.85)",
  "&:hover": {
    bgcolor: "rgba(255,255,255,0.1)",
    color: "common.white",
  },
};

export const LANDING_SECTION_IDS = new Set([
  "education",
  "products",
  "pricing",
  "reviews",
  "catalogue",
  "testimonials",
]);

/** Offset anchor targets below the sticky landing header. */
export const LANDING_SECTION_SCROLL_MARGIN = {
  scrollMarginTop: { xs: "80px", sm: "84px", md: "96px", lg: "112px" },
};

const LANDING_HEADER_SELECTOR = "[data-landing-header]";

export const getLandingHeaderOffset = (extraGap = 12) => {
  const header = document.querySelector(LANDING_HEADER_SELECTOR);
  const headerHeight = header?.getBoundingClientRect().height ?? 0;

  if (headerHeight > 0) {
    return headerHeight + extraGap;
  }

  const width = window.innerWidth;
  if (width >= 1200) return 112 + extraGap;
  if (width >= 900) return 96 + extraGap;
  if (width >= 600) return 84 + extraGap;
  return 80 + extraGap;
};

const getLandingSectionScrollTarget = (section) =>
  section.querySelector("h1, h2") || section;

export const scrollToLandingSection = (id, options = {}) => {
  const {
    behavior = "smooth",
    maxAttempts = 40,
    intervalMs = 100,
    offset = getLandingHeaderOffset(),
    onComplete,
    watchLayout = true,
    layoutWatchMs = 3000,
  } = options;

  let cancelled = false;
  let timeoutId;
  let layoutObserver;
  let layoutStableTimer;
  let layoutWatchStopTimer;

  const performScroll = (section) => {
    const target = getLandingSectionScrollTarget(section);
    const top =
      target.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior,
    });
  };

  const setupLayoutWatch = () => {
    if (!watchLayout || cancelled || typeof ResizeObserver === "undefined") {
      return;
    }

    layoutObserver = new ResizeObserver(() => {
      if (cancelled) return;

      clearTimeout(layoutStableTimer);
      layoutStableTimer = window.setTimeout(() => {
        const section = document.getElementById(id);
        if (section && !cancelled) {
          performScroll(section);
        }
      }, 80);
    });

    layoutObserver.observe(document.body);
    layoutWatchStopTimer = window.setTimeout(() => {
      layoutObserver?.disconnect();
      layoutObserver = null;
    }, layoutWatchMs);
  };

  const finishScroll = (section) => {
    performScroll(section);
    setupLayoutWatch();
    onComplete?.();
  };

  const attemptScroll = (attemptsLeft) => {
    if (cancelled) return;

    const section = document.getElementById(id);
    if (section) {
      requestAnimationFrame(() => {
        if (cancelled) return;
        requestAnimationFrame(() => {
          if (cancelled) return;
          finishScroll(section);
        });
      });
      return;
    }

    if (attemptsLeft > 0) {
      timeoutId = window.setTimeout(
        () => attemptScroll(attemptsLeft - 1),
        intervalMs,
      );
    }
  };

  attemptScroll(maxAttempts);

  return () => {
    cancelled = true;
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
    clearTimeout(layoutStableTimer);
    clearTimeout(layoutWatchStopTimer);
    layoutObserver?.disconnect();
  };
};

/** Navigate from any public page to a landing section anchor. */
export const navigateToLandingSection = (navigate, location, sectionId) => {
  if (location.pathname !== "/") {
    navigate("/", {
      state: { scrollTo: sectionId },
    });
    return;
  }

  scrollToLandingSection(sectionId);
};

export function HighlightText({ children }) {
  return (
    <Box component="span" sx={{ color: "secondary.main" }}>
      {children}
    </Box>
  );
}

export const checkListItemSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  color: "grey.600",
  fontSize: "0.875rem",
  lineHeight: 1.5,
};
